import { useContext, useState } from 'react';

import { Loader } from 'components/Loader';
import { LoaderTypes } from 'components/Loader/types';
import { Page, PagePanel } from 'components/Page';
import { TermsAndConditionsModal } from 'components/TermsAndConditionsModal';
import { PersistentStateContext } from 'context/PersistentState';
import { RedirectContext } from 'context/Redirect';
import { TrackingContext } from 'context/Tracking';
import { UserContext } from 'context/User';
import { useEffectOnce } from 'hooks/useEffectOnce';
import { signInRoutes } from 'pages/SignIn/routes';
import { signUpRoutes } from 'pages/SignUp/routes';
import { Endpoints, post } from 'utils/api';
import { getTermsUserNeedsToAccept } from 'utils/legalTerms';
import { useHistory } from 'utils/router';

export const PostSSO = () => {
  const [showTermsModal, setShowTermsModal] = useState(false);
  const [pendingTermsAccept, setPendingTermsAccept] = useState<(() => Promise<void>) | null>(null);
  const [acceptDisabled, setAcceptDisabled] = useState(false);
  const [pendingTerms, setPendingTerms] = useState<string[]>([]);
  const [
    {
      userMeta: { userIsLoggedIn, userHasConfirmedEmail, userHasOrganisations },
    },
    { signOut },
  ] = useContext(UserContext);
  const [
    { instance, to, customContent: redirectCustomContent },
    { redirectToStateInstanceHome, redirectToTo, redirectToPortalOrInstanceHome, redirectToCustomLoginRedirect },
  ] = useContext(RedirectContext);
  const [, { faro }] = useContext(TrackingContext);
  const [persistentState] = useContext(PersistentStateContext);
  const history = useHistory();

  // RedirectProvider's useEffectOnce may not have fired yet when this effect runs
  // (React effects fire children-first), so fall back to PersistentStateContext
  // which is guaranteed to be initialized before children render.
  const customContent = redirectCustomContent ?? persistentState?.customContent;

  useEffectOnce(() => {
    if (!userIsLoggedIn) {
      history.push(signInRoutes.base);
      return;
    }

    const performRedirect = () => {
      if (!userHasConfirmedEmail) {
        history.push(signUpRoutes.checkInbox);
      } else if (customContent?.loginRedirect && userHasOrganisations) {
        redirectToCustomLoginRedirect();
      } else if (to && !customContent) {
        redirectToTo();
      } else if (instance) {
        redirectToStateInstanceHome();
      } else if (!userHasOrganisations) {
        history.push(signUpRoutes.selectOrganisation);
      } else {
        redirectToPortalOrInstanceHome();
      }
    };

    const checkTermsAcceptanceAndRedirect = async () => {
      const forceTerms = sessionStorage.getItem('forceTerms') === '1';
      sessionStorage.removeItem('forceTerms');
      try {
        const result = await getTermsUserNeedsToAccept(forceTerms);
        if (!result) {
          performRedirect();
          return;
        }
        const { latestTerms, termsToAccept } = result;
        if (termsToAccept.length > 0) {
          setPendingTerms(termsToAccept);
          setPendingTermsAccept(() => async () => {
            try {
              await post(Endpoints.ACCEPT_LEGAL_TERMS, { legalTermsVersion: latestTerms });
            } catch (err) {
              faro.trackError(err);
            }
            performRedirect();
          });
          setShowTermsModal(true);
        } else {
          performRedirect();
        }
      } catch {
        performRedirect();
      }
    };

    void checkTermsAcceptanceAndRedirect();
  });

  const handleTermsAccept = async () => {
    setAcceptDisabled(true);
    try {
      await pendingTermsAccept?.();
      setShowTermsModal(false);
    } catch (err) {
      faro.trackError(err);
      setShowTermsModal(false);
    } finally {
      setAcceptDisabled(false);
    }
  };

  const handleTermsLogout = () => {
    setShowTermsModal(false);
    setPendingTermsAccept(null);
    setPendingTerms([]);
    setAcceptDisabled(false);
    signOut(signInRoutes.base);
  };

  if (!userIsLoggedIn) {
    return null;
  }

  return (
    <>
      <Page heading="Loading your account" title="Loading your account">
        <PagePanel id="panel">
          <Loader id="loader" label="This should not take longer than a few seconds." type={LoaderTypes.SPINNER} />
        </PagePanel>
      </Page>
      <TermsAndConditionsModal
        acceptDisabled={acceptDisabled}
        isOpen={showTermsModal}
        onAccept={handleTermsAccept}
        onLogout={handleTermsLogout}
        pendingTerms={pendingTerms}
      />
    </>
  );
};
